"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RECEIPT_APPLICATION_SERIES = exports.CUSTOMER_RECEIPT_SERIES = void 0;
exports.resolveWithholding = resolveWithholding;
exports.withholdingSnapshot = withholdingSnapshot;
exports.receiptSettlementTotal = receiptSettlementTotal;
exports.customerReceiptPostingIntent = customerReceiptPostingIntent;
exports.planReceiptApplication = planReceiptApplication;
exports.receiptApplicationIntent = receiptApplicationIntent;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const settlement_1 = require("../settlement/settlement");
/** سلسلة إيصالات التحصيل — مستقلة كالفواتير، والصيغة `BLOCKED-ACCOUNTING`. */
exports.CUSTOMER_RECEIPT_SERIES = 'RC';
/** سلسلة تسويات الدفعات المقدمة على الفواتير — مستند مستقل بقيده. */
exports.RECEIPT_APPLICATION_SERIES = 'RA';
/**
 * يحسم مبلغ الخصم من المنبع من مدخلات المستخدم. النسبة لا تأتى من الكود: يدخلها
 * المستخدم أو يختارها من قائمة فى إعدادات شركته (`BLOCKED-TAX` حتى يعتمدها مستشار).
 * المبلغ من النسبة يُقرّب لقرشين؛ وإن أُدخلا معًا واختلفا يُرفض بدل أن يُختار أحدهما.
 */
function resolveWithholding(input) {
    if (!input)
        return { ok: true, value: null };
    const hasAmount = input.amount !== undefined && input.amount !== null;
    const hasRate = input.ratePercent !== undefined && input.ratePercent !== null;
    const hasBase = input.base !== undefined && input.base !== null;
    if (!hasAmount && !hasRate && !hasBase)
        return { ok: true, value: null };
    const issues = [];
    let computed = null;
    if (hasRate || hasBase) {
        if (!(hasRate && hasBase) && !hasAmount) {
            issues.push({
                code: 'WITHHOLDING_INPUT_MISSING',
                message: 'withholding needs an amount, or a rate together with its base',
            });
        }
        if (hasRate && !money_1.Money.of(input.ratePercent).isPositive()) {
            issues.push({
                code: 'WITHHOLDING_NOT_POSITIVE',
                message: 'withholding rate must be positive',
            });
        }
        if (hasBase && !money_1.Money.of(input.base).isPositive()) {
            issues.push({
                code: 'WITHHOLDING_NOT_POSITIVE',
                message: 'withholding base must be positive',
            });
        }
        if (hasRate && hasBase && issues.length === 0) {
            computed = money_1.Money.of(input.base).mul(input.ratePercent).div('100').round(2, 'half_up');
        }
    }
    const amount = hasAmount ? money_1.Money.of(input.amount) : computed;
    if (amount && !amount.isPositive()) {
        issues.push({
            code: 'WITHHOLDING_NOT_POSITIVE',
            message: 'withholding amount must be positive',
        });
    }
    if (hasAmount && computed && !computed.eq(input.amount)) {
        issues.push({
            code: 'WITHHOLDING_AMOUNT_MISMATCH',
            message: 'withholding amount does not match base × rate',
            details: { computed: computed.toFixed(2) },
        });
    }
    if (issues.length > 0 || !amount)
        return { ok: false, issues };
    return {
        ok: true,
        value: {
            amount,
            ratePercent: hasRate ? money_1.Money.of(input.ratePercent).toString() : null,
            base: hasBase ? money_1.Money.of(input.base) : null,
        },
    };
}
/**
 * الأثر الضريبى لسطر الخصم من المنبع. لا قاعدة مُصدَّرة له بعد، فالإصدار `manual`
 * والنسبة والوعاء كما أدخلهما المستخدم — فارغان إن لم يُدخلا، لا مُخترعان.
 */
function withholdingSnapshot(withholding, postingDate, ruleId = 'withholding_on_receipt') {
    return {
        ruleId,
        ruleVersion: 'manual',
        treatment: 'withholding',
        ratePercent: withholding.ratePercent ?? '',
        base: (withholding.base ?? money_1.Money.zero()).toString(),
        amount: withholding.amount.toString(),
        effectiveFrom: postingDate,
        recoverable: true,
    };
}
/** ما يسدده الإيصال من ذمة العميل: النقد مع الخصم من المنبع. */
function receiptSettlementTotal(amount, withholding) {
    return money_1.Money.of(amount).add(withholding?.amount ?? money_1.Money.zero());
}
/**
 * قيد التحصيل: مدين النقدية بالمقبوض، ومدين «ضرائب خصم من المنبع» بما خصمه العميل،
 * ودائن العملاء سطرًا لكل فاتورة بمرجعها. الباقى غير المخصص دائن على «دفعات مقدمة من
 * العملاء» أو على ذمة العميل حسب الإعداد. لا إيراد هنا — التحصيل يُقفل ذمة نشأت عند
 * الفاتورة، والدفعة المقدمة التزام حتى تُسوّى على فاتورة.
 */
function customerReceiptPostingIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const lines = [
        (0, document_rules_1.debitLine)(input.depositAccount, amount, ctx.currency, { cashboxId: input.cashboxId ?? null }),
    ];
    if (input.withholding && input.withholding.amount.isPositive()) {
        lines.push((0, document_rules_1.debitLine)(ctx.accounts.withholdingReceivable, input.withholding.amount, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId, allocationRef: null },
            tax: withholdingSnapshot(input.withholding, input.postingDate),
        }));
    }
    for (const allocation of input.plan.allocations) {
        lines.push((0, document_rules_1.creditLine)(ctx.accounts.receivable, allocation.amount, ctx.currency, {
            counterparty: {
                type: 'customer',
                id: input.customerId,
                allocationRef: allocation.documentId,
            },
        }));
    }
    if (input.plan.unallocated.isPositive()) {
        const advanceAccount = input.advanceMode === 'separate_account'
            ? ctx.accounts.customerAdvance
            : ctx.accounts.receivable;
        lines.push((0, document_rules_1.creditLine)(advanceAccount, input.plan.unallocated, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId, allocationRef: null },
        }));
    }
    return {
        companyId: ctx.companyId,
        sourceType: 'customer_receipt',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
/**
 * تخطيط تسوية دفعة مقدمة (أو باقٍ غير مخصص) على فواتير لاحقة. نفس قواعد التخصيص فى
 * `planSettlement` — لا فوق متبقى الفاتورة ولا فوق المتاح على الإيصال ولا لعميل آخر —
 * مع ترتيب التواريخ: التسوية لا تسبق الإيصال ولا الفاتورة. كل المشاكل مرة واحدة.
 */
function planReceiptApplication(input) {
    const issues = [];
    const available = money_1.Money.of(input.available);
    if (input.applicationDate < input.receiptDate) {
        issues.push({
            code: 'APPLICATION_BEFORE_RECEIPT',
            message: 'application date is before the receipt date ' + input.receiptDate,
        });
    }
    if (!available.isPositive()) {
        issues.push({
            code: 'ADVANCE_NOT_AVAILABLE',
            message: 'nothing is left unallocated on this receipt',
        });
    }
    input.requests.forEach((request, lineIndex) => {
        const issued = input.issueDates.get(request.documentId);
        if (issued && issued > input.applicationDate) {
            issues.push({
                code: 'APPLICATION_BEFORE_INVOICE',
                message: 'application date is before the invoice issue date ' + issued,
                lineIndex,
            });
        }
    });
    // المتاح غير الموجب سُجّل أعلاه بكوده الأوضح؛ نخطط على موجب حتى تُجمع بقية المشاكل
    const plan = (0, settlement_1.planSettlement)(input.customerId, available.isPositive() ? available : '0.01', input.requests, input.openDocuments);
    if (!plan.ok) {
        issues.push(...plan.issues.filter((issue) => available.isPositive() || issue.code !== 'ALLOCATION_EXCEEDS_AMOUNT'));
    }
    if (issues.length > 0 || !plan.ok)
        return { ok: false, issues };
    return {
        ok: true,
        value: {
            allocations: plan.value.allocations,
            allocated: plan.value.allocated,
            remaining: available.sub(plan.value.allocated),
        },
    };
}
/**
 * قيد تسوية الدفعة: مدين حيث يقف الباقى (الدفعات المقدمة، أو ذمة العميل غير المخصصة)
 * بمرجع الإيصال، ودائن العملاء سطرًا لكل فاتورة بمرجعها. فى وضع ذمة العميل الطرفان
 * على نفس الحساب: الرصيد لا يتغير، لكن الدفتر يحفظ أى فاتورة سُدّدت بأى دفعة.
 */
function receiptApplicationIntent(ctx, input) {
    const total = money_1.Money.sum(input.allocations.map((allocation) => allocation.amount));
    const source = input.advanceMode === 'separate_account'
        ? ctx.accounts.customerAdvance
        : ctx.accounts.receivable;
    const lines = [
        (0, document_rules_1.debitLine)(source, total, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId, allocationRef: input.receiptId },
        }),
        ...input.allocations.map((allocation) => (0, document_rules_1.creditLine)(ctx.accounts.receivable, allocation.amount, ctx.currency, {
            counterparty: {
                type: 'customer',
                id: input.customerId,
                allocationRef: allocation.documentId,
            },
        })),
    ];
    return {
        companyId: ctx.companyId,
        sourceType: 'receipt_application',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
//# sourceMappingURL=receipt.js.map